import {
  MEAL_SLOT_ORDER,
  mealLogSchema,
  mealPlanSchema,
  plannedMealSchema,
  type MealLog,
  type MealPlan,
  type MealSlot,
  type PlannedMeal,
  type PlannedMealStatus,
  type SyncColumns,
} from '@cadencia/contracts';
import { logger } from '../../lib/logger.js';
import { addDays } from '../../lib/dates.js';
import { db } from '../db.js';
import { nowIso } from '../ids.js';
import { newSyncColumns, putNewRows, putRow, type LocalTable, type WriteContext } from '../writes.js';
import { parchear } from '../parchear.js';

/**
 * Menu semanal y registro de comidas.
 *
 * Tres tablas encadenadas: `mealPlans` -> `plannedMeals` -> `mealLogs`. El
 * orden importa al sincronizar y lo fija `ORDEN_DE_ENVIO`.
 *
 * Como en `nutritionRepo`, al leer se usa `safeParse` (ADR-035) y al escribir
 * `parse`.
 */

function normalizar<T>(
  filas: readonly unknown[],
  esquema: { safeParse: (value: unknown) => { success: boolean; data?: unknown } },
  tabla: string,
): T[] {
  const salida: T[] = [];
  let invalidas = 0;
  for (const fila of filas) {
    const resultado = esquema.safeParse(fila);
    salida.push((resultado.success ? resultado.data : fila) as T);
    if (!resultado.success) invalidas += 1;
  }
  if (invalidas > 0) {
    logger.warn('db', 'NUTRITION_SHAPE_MISMATCH', 'Filas locales que no encajan con su contrato', {
      tabla,
      invalidas,
    });
  }
  return salida;
}

const ordenDeSlot = (slot: MealSlot): number => MEAL_SLOT_ORDER.indexOf(slot);

// --------------------------------------------------------------------------
// Plan semanal
// --------------------------------------------------------------------------

export async function getPlanOfWeek(
  userId: string,
  weekStart: string,
): Promise<MealPlan | undefined> {
  const filas = await db.mealPlans.where('weekStart').equals(weekStart).toArray();
  const vivas = filas.filter(
    (fila) => fila.userId === userId && fila.deletedAt === null && fila.status !== 'archived',
  );
  return normalizar<MealPlan>(vivas, mealPlanSchema, 'mealPlans')[0];
}

export async function listPlannedMealsOfPlan(mealPlanId: string): Promise<PlannedMeal[]> {
  const filas = await db.plannedMeals.where('mealPlanId').equals(mealPlanId).toArray();
  const vivas = filas.filter((fila) => fila.deletedAt === null);
  return normalizar<PlannedMeal>(vivas, plannedMealSchema, 'plannedMeals').sort(
    (a, b) => a.mealDate.localeCompare(b.mealDate) || ordenDeSlot(a.slot) - ordenDeSlot(b.slot),
  );
}

export async function listPlannedMealsOfDay(
  userId: string,
  date: string,
): Promise<PlannedMeal[]> {
  const filas = await db.plannedMeals.where('mealDate').equals(date).toArray();
  const vivas = filas.filter((fila) => fila.userId === userId && fila.deletedAt === null);
  return normalizar<PlannedMeal>(vivas, plannedMealSchema, 'plannedMeals').sort(
    (a, b) => ordenDeSlot(a.slot) - ordenDeSlot(b.slot),
  );
}

/**
 * Las comidas planificadas de un rango de dias, ambos extremos incluidos.
 *
 * La lista de la compra se genera sobre un rango, no sobre una semana: el §9.1
 * de PRODUCT_SPEC.md permite «semana completa o rango de dias seleccionado», y
 * comprar para los tres dias que quedan es un caso normal.
 */
export async function listPlannedMealsBetween(
  userId: string,
  desde: string,
  hasta: string,
): Promise<PlannedMeal[]> {
  const filas = await db.plannedMeals.where('userId').equals(userId).toArray();
  const vivas = filas.filter(
    (fila) => fila.deletedAt === null && fila.mealDate >= desde && fila.mealDate <= hasta,
  );
  return normalizar<PlannedMeal>(vivas, plannedMealSchema, 'plannedMeals').sort(
    (a, b) => a.mealDate.localeCompare(b.mealDate) || ordenDeSlot(a.slot) - ordenDeSlot(b.slot),
  );
}

export async function getPlannedMeal(id: string): Promise<PlannedMeal | undefined> {
  const fila = await db.plannedMeals.get(id);
  if (fila === undefined || fila.deletedAt !== null) return undefined;
  return normalizar<PlannedMeal>([fila], plannedMealSchema, 'plannedMeals')[0];
}

export interface PlannedMealInput {
  mealDate: string;
  slot: MealSlot;
  recipeSlug: string | null;
  items: PlannedMeal['items'];
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  status?: PlannedMealStatus;
  adjustedByEngine?: boolean;
  adjustmentReason?: string | null;
}

/**
 * Guarda el menu de una semana entera: el plan y sus comidas, en una sola
 * pasada.
 *
 * Si ya habia un plan para esa semana se ARCHIVA junto con sus comidas en vez
 * de borrarse. Regenerar el menu del jueves no puede hacer desaparecer lo que
 * el usuario ya confirmo el lunes: los `meal_logs` apuntan a `planned_meals`
 * por identificador, y una comida registrada cuyo plan ha dejado de existir es
 * un registro que no se puede explicar.
 *
 * `preserveFrom` (opcional) LLEVA las comidas anteriores a esa fecha al plan
 * nuevo en vez de enterrarlas.
 *
 * POR QUE NO BASTA CON ARCHIVAR
 *
 * Archivar el plan y poner lapida a TODAS sus comidas es lo correcto cuando se
 * rehace la semana entera: se rehace todo, y lo viejo deja de valer. Deja de
 * serlo cuando lo que cambia es el esquema de comidas a mitad de semana, que es
 * el caso nuevo (§32 del encargo): los dias que ya se han comido no se
 * regeneran, asi que sus comidas tienen que seguir vivas y seguir siendo LAS
 * MISMAS FILAS. Con filas nuevas, el `planned_meal_id` de cada `meal_log` del
 * lunes apuntaria a una lapida, y el historial de lo consumido dejaria de poder
 * explicarse contra su plan.
 *
 * Por eso se reescriben con el `mealPlanId` nuevo conservando su `id`: cambian
 * de plan, no de identidad.
 */
export async function savePlanWithMeals(
  ctx: WriteContext,
  weekStart: string,
  comidas: readonly PlannedMealInput[],
  opciones: {
    varietyWindowDays: number;
    generatedBy?: MealPlan['generatedBy'];
    /** Las comidas de fechas ANTERIORES a esta se conservan tal cual. */
    preserveFrom?: string;
  },
): Promise<{ plan: MealPlan; meals: PlannedMeal[] }> {
  const previo = await getPlanOfWeek(ctx.userId, weekStart);
  const conservadas: PlannedMeal[] = [];
  const anteriores = previo === undefined ? [] : await listPlannedMealsOfPlan(previo.id);

  const plan = mealPlanSchema.parse({
    ...newSyncColumns(ctx),
    weekStart,
    status: 'active',
    generatedBy: opciones.generatedBy ?? 'algorithm',
    varietyWindowDays: opciones.varietyWindowDays,
  });

  const corte = opciones.preserveFrom;
  for (const comida of anteriores) {
    if (corte !== undefined && comida.mealDate < corte) {
      // Misma fila, plan nuevo. Ver la cabecera.
      conservadas.push(await putRow(ctx, 'plannedMeals', { ...comida, mealPlanId: plan.id }));
    } else {
      await putRow(ctx, 'plannedMeals', { ...comida, deletedAt: nowIso() });
    }
  }
  if (previo !== undefined) {
    await putRow(ctx, 'mealPlans', { ...previo, status: 'archived' });
  }

  const filas = comidas.map((comida) =>
    plannedMealSchema.parse({
      ...newSyncColumns(ctx),
      mealPlanId: plan.id,
      mealDate: comida.mealDate,
      slot: comida.slot,
      recipeSlug: comida.recipeSlug,
      items: comida.items,
      kcal: comida.kcal,
      proteinG: comida.proteinG,
      carbsG: comida.carbsG,
      fatG: comida.fatG,
      status: comida.status ?? 'planned',
      adjustedByEngine: comida.adjustedByEngine ?? false,
      adjustmentReason: comida.adjustmentReason ?? null,
    }),
  );

  const entries: { table: LocalTable; row: SyncColumns }[] = [
    { table: 'mealPlans', row: plan },
    ...filas.map((row) => ({ table: 'plannedMeals' as LocalTable, row })),
  ];
  await putNewRows(ctx, entries);

  const meals = [...conservadas, ...filas].sort(
    (a, b) => a.mealDate.localeCompare(b.mealDate) || ordenDeSlot(a.slot) - ordenDeSlot(b.slot),
  );
  return { plan, meals };
}

/** Reescribe una comida planificada (cambio de estado, reajuste del motor). */
export async function updatePlannedMeal(
  ctx: WriteContext,
  meal: PlannedMeal,
  parche: Partial<Omit<PlannedMeal, keyof ReturnType<typeof newSyncColumns>>>,
): Promise<PlannedMeal> {
  const row = parchear(plannedMealSchema, meal, parche, {
    tabla: 'planned_meals',
    id: meal.id,
  });
  return putRow(ctx, 'plannedMeals', row);
}

// --------------------------------------------------------------------------
// Registro
// --------------------------------------------------------------------------

export async function listMealLogsOfDay(userId: string, date: string): Promise<MealLog[]> {
  const filas = await db.mealLogs.where('mealDate').equals(date).toArray();
  const vivas = filas.filter((fila) => fila.userId === userId && fila.deletedAt === null);
  return normalizar<MealLog>(vivas, mealLogSchema, 'mealLogs').sort(
    (a, b) => ordenDeSlot(a.slot) - ordenDeSlot(b.slot),
  );
}

export async function listMealLogsBetween(
  userId: string,
  from: string,
  toExclusive: string,
): Promise<MealLog[]> {
  const filas = await db.mealLogs.where('userId').equals(userId).toArray();
  const vivas = filas.filter(
    (fila) => fila.deletedAt === null && fila.mealDate >= from && fila.mealDate < toExclusive,
  );
  return normalizar<MealLog>(vivas, mealLogSchema, 'mealLogs').sort(
    (a, b) => a.mealDate.localeCompare(b.mealDate) || ordenDeSlot(a.slot) - ordenDeSlot(b.slot),
  );
}

export interface MealLogInput {
  plannedMealId: string | null;
  mealDate: string;
  slot: MealSlot;
  items: MealLog['items'];
  kcal: number;
  proteinG: number;
  carbsG: number;
  fatG: number;
  recipeSnapshot: Record<string, unknown> | null;
  entryMode: MealLog['entryMode'];
}

/**
 * Registra lo comido en un hueco.
 *
 * **Un hueco y un dia, una fila** (ADR-038). «Comere fuera» y el registro
 * posterior de lo que se comio fuera son la misma cena en dos momentos: como
 * filas distintas, el dia contaria esa cena dos veces y el reajuste corregiria
 * un desvio que no existe. El indice unico de la migracion 0017 lo impone
 * tambien en el servidor, no solo aqui.
 */
export async function logMeal(ctx: WriteContext, input: MealLogInput): Promise<MealLog> {
  const previos = await listMealLogsOfDay(ctx.userId, input.mealDate);
  const previo = previos.find((fila) => fila.slot === input.slot);

  const row =
    previo === undefined
      ? mealLogSchema.parse({ ...newSyncColumns(ctx), ...input, loggedAt: nowIso() })
      : parchear(mealLogSchema, previo, { ...input, loggedAt: nowIso() }, {
          tabla: 'meal_logs',
          id: previo.id,
        });
  return putRow(ctx, 'mealLogs', row);
}

/** Deshace un registro. Borrado suave, como todo lo sincronizable. */
export async function unlogMeal(ctx: WriteContext, log: MealLog): Promise<void> {
  await putRow(ctx, 'mealLogs', { ...log, deletedAt: nowIso() });
}

/** Dias de la semana que ya tienen menu, para saber si hace falta generarlo. */
export async function daysWithPlan(userId: string, weekStart: string): Promise<Set<string>> {
  const plan = await getPlanOfWeek(userId, weekStart);
  if (plan === undefined) return new Set();
  const comidas = await listPlannedMealsOfPlan(plan.id);
  return new Set(comidas.map((comida) => comida.mealDate));
}

/** Las comidas de la semana que empieza en `weekStart`, por dia. */
export async function plannedMealsByDay(
  userId: string,
  weekStart: string,
): Promise<Map<string, PlannedMeal[]>> {
  const salida = new Map<string, PlannedMeal[]>();
  for (let i = 0; i < 7; i += 1) {
    const dia = addDays(weekStart, i);
    salida.set(dia, await listPlannedMealsOfDay(userId, dia));
  }
  return salida;
}
